// frontend/src/App.js
import React, { useState, useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import LoginPage from "./pages/LoginPage";
import TimeEntryPage from "./pages/TimeEntryPage";
import ClientsPage from "./pages/ClientsPage";
import InvoicesPage from "./pages/InvoicesPage";
import InvoiceDetailPage from "./pages/InvoiceDetailPage";
import DataExplorerPage from "./pages/DataExplorerPage";
import AdminSettingsPage from "./pages/AdminSettingsPage";
import ClientDetailPage from "./pages/ClientDetailPage";
import ClientGroupPage from "./pages/ClientGroupPage";
import TopBar from "./components/TopBar";
import LeftNav from "./pages/_LeftNav";  // Add this import
import { authMe } from "./api";
import CreateInvoicePage from "./pages/CreateInvoicePage";
import EmployeesPage from './pages/employees/EmployeesPage';
import TimeCodesPage from './pages/TimeCodesPage';
import HrDashboardPage from "./pages/HrDashboardPage";
import WorkflowPage from './pages/WorkflowPage';
import TasksPage from './pages/TasksPage';
import TaskDetailPage from './pages/TaskDetailPage';
import OtherTaskDetailPage from './pages/OtherTaskDetailPage';
import TimeApprovalPage from './pages/TimeApprovalPage';
import QuickBooksMappingPage from "./pages/QuickBooksMappingPage";
import InvoiceConflictPage from "./pages/InvoiceConflictPage";
import ReconcilePage from "./pages/invoices/ReconcilePage";
import ArchivedPage from "./pages/ArchivedPage";
import RollForwardRunPage from "./pages/RollForwardRunPage";
import PlanningPage from "./pages/PlanningPage";
import ReviewPage from "./pages/ReviewPage";
import ImprovementsPage from "./pages/improvements/ImprovementsPage";
import ImprovementDetailPage from "./pages/improvements/ImprovementDetailPage";
import FeedbackButton from "./components/feedback/FeedbackButton";

function Protected({ children, user, setUser }) {
  const [checked, setChecked] = useState(false);
  const [authed, setAuthed] = useState(false); 

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const me = await authMe();
        if (mounted) {
          setAuthed(true);
          setChecked(true);
          if (!user && setUser) {
            setUser(me);
          }
          console.log("authMe success:", me);
        }
      } catch (err) {
        if (mounted) {
          setAuthed(false);
          setChecked(true);
          if (setUser) {
            setUser(null);
          }
          console.log("authMe failed:", err);
        }
      }
    })();
    return () => {
      mounted = false;
    };
  }, [user, setUser]);

  if (!checked) return <div>Loading...</div>;
  if (!authed) return <Navigate to="/login" replace />;
  return children;
}

export default function App() {
  const [user, setUser] = useState(null);
  const [initializing, setInitializing] = useState(true);
  const [isNavCollapsed, setIsNavCollapsed] = useState(() => {
    return localStorage.getItem("leftNavCollapsed") === "true";
  });

  // Listen for nav collapse changes
  useEffect(() => {
    const checkCollapse = () => {
      const collapsed = localStorage.getItem("leftNavCollapsed") === "true";
      setIsNavCollapsed(collapsed);
    };
    
    // Check every 100ms for collapse state changes
    const interval = setInterval(checkCollapse, 100);
    
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    // Always force dev mode to manager
    if (process.env.NODE_ENV === "development") {
      sessionStorage.setItem("userRole", "MANAGER");
    }
  }, []);

  useEffect(() => {
    fetch("/api/auth/csrf/", { credentials: "include" });
  }, []);

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const me = await authMe();
        if (mounted) {
          setUser(me);
        }
      } catch (err) {
        console.log("Initial auth check failed:", err);
      } finally {
        if (mounted) {
          setInitializing(false);
        }
      }
    })();
    return () => {
      mounted = false;
    };
  }, []);

  const handleLogout = () => {
    setUser(null);
  };

  if (initializing) {
    return <div>Loading...</div>;
  }

  return (
    <BrowserRouter>
      {user && <TopBar user={user} onLogout={handleLogout} />}
      
      {/* Fixed LeftNav that stays in place */}
      {user && <LeftNav user={user} />}
      {user && <FeedbackButton />}
      
      {/* Main content area with dynamic margin based on nav state */}
      <div style={{ 
        marginLeft: user ? (isNavCollapsed ? 70 : 220) : 0,
        marginTop: user ? 64 : 0, // Adjust to your TopBar height
        minHeight: user ? 'calc(100vh - 64px)' : '100vh',
        transition: 'margin-left 0.3s ease',
      }}>
        <Routes>
          <Route
            path="/login"
            element={
              user ? (
                <Navigate to="/time" replace />
              ) : (
                <LoginPage onLogin={setUser} />
              )
            }
          />

          <Route
            path="/"
            element={<Navigate to={user ? "/time" : "/login"} replace />}
          />

          <Route path="/hr-dashboard" element={<HrDashboardPage user={user} />} />

          <Route 
            path="/workflow" 
            element={<WorkflowPage user={user} />} 
          />

          <Route
            path="/time"
            element={
              <Protected user={user} setUser={setUser}>
                <TimeEntryPage user={user} />
              </Protected>
            }
          />
          <Route
            path="/time-approval"
            element={
              <Protected user={user} setUser={setUser}>
                <TimeApprovalPage />
              </Protected>
            }
          />
          <Route
            path="/archived"
            element={
              <Protected user={user} setUser={setUser}>
                <ArchivedPage user={user} />
              </Protected>
            }
          />
          <Route
            path="/clients"
            element={
              <Protected user={user} setUser={setUser}>
                <ClientsPage user={user} />
              </Protected>
            }
          />
          <Route
            path="/clients/groups/:groupId"
            element={
              <Protected user={user} setUser={setUser}>
                <ClientGroupPage user={user} />
              </Protected>
            }
          />
          <Route
            path="/clients/:id"
            element={
              <Protected user={user} setUser={setUser}>
                <ClientDetailPage user={user} />
              </Protected>
            }
          />
          <Route
            path="/tasks"
            element={
              <Protected user={user} setUser={setUser}>
                <TasksPage user={user} />
              </Protected>
            }
          />
          <Route
            path="/tasks/:id"
            element={
              <Protected user={user} setUser={setUser}>
                <TaskDetailPage user={user} />
              </Protected>
            }
          />
          <Route
            path="/other-tasks/:id"
            element={
              <Protected user={user} setUser={setUser}>
                <OtherTaskDetailPage user={user} />
              </Protected>
            }
          />
          <Route
            path="/invoices"
            element={
              <Protected user={user} setUser={setUser}>
                <InvoicesPage user={user} />
              </Protected>
            }
          />
          <Route
            path="/invoices/create"
            element={
              <Protected user={user} setUser={setUser}>
                <CreateInvoicePage user={user} />
              </Protected>
            }
          />
          <Route
            path="/invoices/reconcile"
            element={
              <Protected user={user} setUser={setUser}>
                <ReconcilePage user={user} />
              </Protected>
            }
          />
          <Route
            path="/invoices/:id"
            element={
              <Protected user={user} setUser={setUser}>
                <InvoiceDetailPage user={user} />
              </Protected>
            }
          />
          <Route
            path="/invoices/:id/conflict"
            element={
              <Protected user={user} setUser={setUser}>
                <InvoiceConflictPage user={user} />
              </Protected>
            }
          />
          <Route
            path="/explore"
            element={
              <Protected user={user} setUser={setUser}>
                <DataExplorerPage user={user} />
              </Protected>
            }
          />
          <Route
            path="/employees"
            element={
              <Protected user={user} setUser={setUser}>
                <EmployeesPage user={user} />
              </Protected>
            }
          />
          <Route
            path="/time-codes"
            element={
              <Protected user={user} setUser={setUser}>
                <TimeCodesPage user={user} />
              </Protected>
            }
          />
          <Route
            path="/settings"
            element={
              <Protected user={user} setUser={setUser}>
                <AdminSettingsPage user={user} />
              </Protected>
            }
          />
          <Route
            path="/settings/quickbooks/customers"
            element={
              <Protected user={user} setUser={setUser}>
                <QuickBooksMappingPage />
              </Protected>
            }
          />
          <Route
            path="/settings/roll-forward"
            element={
              <Protected user={user} setUser={setUser}>
                <RollForwardRunPage user={user} />
              </Protected>
            }
          />
          <Route
            path="/planning"
            element={
              <Protected user={user} setUser={setUser}>
                <PlanningPage user={user} />
              </Protected>
            }
          />
          <Route
            path="/review"
            element={
              <Protected user={user} setUser={setUser}>
                <ReviewPage user={user} />
              </Protected>
            }
          />

          <Route
            path="/improvements"
            element={
              <Protected user={user} setUser={setUser}>
                <ImprovementsPage user={user} />
              </Protected>
            }
          />
          <Route
            path="/improvements/:id"
            element={
              <Protected user={user} setUser={setUser}>
                <ImprovementDetailPage />
              </Protected>
            }
          />

          <Route
            path="*"
            element={<Navigate to={user ? "/time" : "/login"} replace />}
          />
        </Routes>
      </div>
    </BrowserRouter>
  );
}